import React from 'react';
import { D3TreeNode, Person } from '../../types';

/**
 * TreeNodeSVG Component
 *
 * Renders an individual person node in the D3 family tree
 * Uses SVG elements for high-performance rendering
 *
 * Features:
 * - Person info (name, years)
 * - Avatar with gender-based colors
 * - Expand/collapse indicator
 * - Hover effects
 * - Highlight states
 *
 * @component
 */
interface TreeNodeSVGProps {
  /** D3 tree node with position data */
  node: D3TreeNode;

  /** Node width */
  width?: number;

  /** Node height */
  height?: number;

  /** Click handler */
  onClick?: (node: D3TreeNode, event: React.MouseEvent) => void;

  /** Double-click handler */
  onDoubleClick?: (node: D3TreeNode, event: React.MouseEvent) => void;

  /** Mouse enter handler */
  onMouseEnter?: (node: D3TreeNode, event: React.MouseEvent) => void;

  /** Mouse leave handler */
  onMouseLeave?: (node: D3TreeNode, event: React.MouseEvent) => void;

  /** Context menu handler */
  onContextMenu?: (node: D3TreeNode, event: React.MouseEvent) => void;

  /** Is node highlighted */
  isHighlighted?: boolean;

  /** Is node in lineage path */
  isInLineage?: boolean;

  /** Is node selected */
  isSelected?: boolean;

  /** Enable expand/collapse */
  enableCollapse?: boolean;
}

export const TreeNodeSVG: React.FC<TreeNodeSVGProps> = ({
  node,
  width = 160,
  height = 70,
  onClick,
  onDoubleClick,
  onMouseEnter,
  onMouseLeave,
  onContextMenu,
  isHighlighted = false,
  isInLineage = false,
  isSelected = false,
  enableCollapse = true,
}) => {
  const person = node.data.person;
  const hasChildren = node.children && node.children.length > 0;
  const hasCollapsedChildren = node._children && node._children.length > 0;
  const canCollapse = enableCollapse && (hasChildren || hasCollapsedChildren);

  // ===== Event Handlers =====

  const handleClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    onClick?.(node, event);
  };

  const handleDoubleClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    onDoubleClick?.(node, event);
  };

  const handleMouseEnter = (event: React.MouseEvent) => {
    onMouseEnter?.(node, event);
  };

  const handleMouseLeave = (event: React.MouseEvent) => {
    onMouseLeave?.(node, event);
  };

  const handleContextMenu = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    onContextMenu?.(node, event);
  };

  // ===== Calculated Values =====

  const getGenderColor = (gender?: string): string => {
    switch (gender) {
      case 'male':
        return '#4A90E2';
      case 'female':
        return '#E24A90';
      default:
        return '#9B9B9B';
    }
  };

  const getBorderColor = (): string => {
    if (isSelected) return '#FF6B35';
    if (isHighlighted) return '#FFD700';
    if (isInLineage) return '#90EE90';
    return '#CCCCCC';
  };

  const getBorderWidth = (): number => {
    if (isSelected || isHighlighted || isInLineage) return 3;
    return 1.5;
  };

  const getBackgroundColor = (): string => {
    if (isSelected) return '#FFF5F0';
    if (isHighlighted) return '#FFFBF0';
    if (isInLineage) return '#F0FFF0';
    return '#FFFFFF';
  };

  const getInitials = (firstName: string, lastName: string): string => {
    const first = firstName?.charAt(0)?.toUpperCase() || '';
    const last = lastName?.charAt(0)?.toUpperCase() || '';
    return first + last;
  };

  const getYearRange = (person: Person): string => {
    const birthYear = person.birthDate ? new Date(person.birthDate).getFullYear() : '?';
    const deathYear = person.deathDate ? new Date(person.deathDate).getFullYear() : '';
    return deathYear ? `${birthYear}-${deathYear}` : `b. ${birthYear}`;
  };

  const getDisplayName = (person: Person): string => {
    const fullName = `${person.firstName} ${person.lastName}`;
    const maxLength = 20;  // Original max length
    if (fullName.length > maxLength) {
      return fullName.substring(0, maxLength - 3) + '...';
    }
    return fullName;
  };

  // ===== Render =====

  return (
    <g
      transform={`translate(${node.x - width / 2}, ${node.y - height / 2})`}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onContextMenu={handleContextMenu}
      style={{ cursor: 'pointer' }}
    >
      {/* Node Container */}
      <rect
        width={width}
        height={height}
        rx={8}
        ry={8}
        fill={getBackgroundColor()}
        stroke={getBorderColor()}
        strokeWidth={getBorderWidth()}
        style={{
          filter: isSelected || isHighlighted ? 'drop-shadow(0 4px 6px rgba(0,0,0,0.2))' : 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
          transition: 'all 0.3s ease',
        }}
      />

      {/* Avatar Circle */}
      <circle
        cx={30}
        cy={height / 2}
        r={20}
        fill={getGenderColor(person.gender)}
        stroke="#FFFFFF"
        strokeWidth={2}
      />

      {/* Initials */}
      <text
        x={30}
        y={height / 2}
        textAnchor="middle"
        dominantBaseline="central"
        fill="#FFFFFF"
        fontSize="14"
        fontWeight="600"
        fontFamily="Arial, sans-serif"
      >
        {getInitials(person.firstName, person.lastName)}
      </text>

      {/* Name */}
      <text
        x={55}
        y={height / 2 - 10}
        fill="#333333"
        fontSize="13"
        fontWeight="600"
        fontFamily="Arial, sans-serif"
      >
        {getDisplayName(person)}
      </text>

      {/* Years */}
      <text
        x={55}
        y={height / 2 + 10}
        fill="#666666"
        fontSize="11"
        fontFamily="Arial, sans-serif"
      >
        {getYearRange(person)}
      </text>

      {/* Expand/Collapse Indicator */}
      {canCollapse && (
        <g transform={`translate(${width - 20}, ${height / 2})`}>
          <circle
            r={10}
            fill={hasCollapsedChildren ? '#4A90E2' : '#E8E8E8'}
            stroke="#CCCCCC"
            strokeWidth={1}
          />
          <text
            textAnchor="middle"
            dominantBaseline="central"
            fill={hasCollapsedChildren ? '#FFFFFF' : '#666666'}
            fontSize="14"
            fontWeight="bold"
            fontFamily="Arial, sans-serif"
          >
            {hasCollapsedChildren ? '+' : '-'}
          </text>
        </g>
      )}

      {/* Spouse Indicator */}
      {node.data.spouses && node.data.spouses.length > 0 && (
        <g transform={`translate(${width - 10}, 10)`}>
          <circle r={6} fill="#FFB6C1" stroke="#FF69B4" strokeWidth={1} />
          <text
            textAnchor="middle"
            dominantBaseline="central"
            fill="#FFFFFF"
            fontSize="8"
            fontWeight="bold"
            fontFamily="Arial, sans-serif"
          >
            {node.data.spouses.length}
          </text>
        </g>
      )}

      {/* Hover Overlay (invisible, for larger hit area) */}
      <rect
        width={width}
        height={height}
        rx={8}
        ry={8}
        fill="transparent"
        style={{ cursor: 'pointer' }}
      />
    </g>
  );
};
